import axios from 'axios';
import toast from 'react-hot-toast';

type MinimalQuote = {
  _id: string;
  reference: string;
  clientName?: string;
  total?: number;
  status: "draft" | "sent" | "accepted" | "rejected" | "expired";
  createdAt: string;
};

type Pagination = { page: number; limit: number; total: number; pages: number };

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
export const BASE_URL = API_BASE_URL.replace('/api', '');

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Log request body for debugging
    if (config.data) {
      console.log(`[Axios Request] ${config.method?.toUpperCase()} ${config.url}:`, config.data);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle token expiration
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      
      // Show toast notification
      toast.error('Votre session a expiré. Veuillez vous reconnecter.');
      
      // Redirect to login page
      /*setTimeout(() => {
        window.location.href = '/login';
      }, 1000);*/
    }
    
    // Handle server errors
    if (error.response?.status === 500) {
      toast.error('Une erreur serveur est survenue. Veuillez réessayer plus tard.');
    }
    
    // Handle network errors
    if (error.message === 'Network Error') {
      toast.error('Problème de connexion au serveur. Vérifiez votre connexion internet.');
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) => 
    api.post('/auth/login', { email, password }),
  
  register: (userData: any) => 
    api.post('/auth/register', userData),
  
  refreshToken: (refreshToken: string) => 
    api.post('/auth/refresh', { refreshToken }),
  
  logout: () => 
    api.post('/auth/logout'),
  
  getProfile: () => 
    api.get('/auth/me'),
  
  updateProfile: (profileData: any) => 
    api.put('/auth/profile', profileData),
  
  changePassword: (currentPassword: string, newPassword: string) => 
    api.put('/auth/change-password', { currentPassword, newPassword }),
  
  verifyToken: () => 
    api.get('/auth/verify')
};

// Users API
export const usersAPI = {
  getAll: async (params?: any): Promise<{ items: any[]; total: number }> => {
    const response = await api.get('/users', { params });
    const { users, pagination } = response.data.data;
    return {
      items: users,
      total: pagination.total,
    };
  },
  
  getTechnicians: async (): Promise<{ items: any[] }> => {
    const response = await api.get('/users/technicians');
    console.log("API: getTechnicians raw response:", response);
    const { technicians } = response.data.data;
    return { items: technicians || [] };
  },
  
  getClients: async (): Promise<{ items: any[] }> => {
    const response = await api.get('/users/clients');
    console.log("API: getClients raw response:", response);
    const { clients } = response.data.data;
    return { items: clients || [] };
  },

  getAllClients: async (): Promise<{ items: any[] }> => {
    const response = await api.get('/users/all-clients');
    const { clients } = response.data.data;
    return { items: clients || [] };
  },
  
  getById: (_id: string) => 
    api.get(`/users/${_id}`),
  
  create: (data) => 
    api.post('/users', data),      // data contient departements: string[]
  update: (id, data) => 
    api.put(`/users/${id}`, data),
  
  delete: (_id: string) => 
    api.delete(`/users/${_id}`),
  
  toggleStatus: (_id: string) => 
    api.patch(`/users/${_id}/toggle-status`),
  
  getStats: () => 
    api.get('/users/stats/overview'),

  getContacts: () => 
    api.get('/users/contacts'),

  updateNotificationSettings: (settings: any) => 
    api.put('/users/me/notification-settings', settings),

  exportCsv: () => 
    api.get('/users/export/csv', { responseType: 'blob' }),

  importCsv: (formData: FormData) => 
    api.post('/users/import/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
};

// Companies API
export const companiesAPI = {
  getAll: (params?: any) => 
    api.get('/companies', { params }),
  
  getById: (_id: string) => 
    api.get(`/companies/${_id}`),
  
  create: (companyData: any) => 
    api.post('/companies', companyData),
  
  update: (id: string, companyData: any) => 
    api.put(`/companies/${id}`, companyData),
  
  delete: (id: string) => 
    api.delete(`/companies/${id}`),
  
  toggleStatus: (id: string) => 
    api.patch(`/companies/${id}/toggle-status`),
  
  search: (query: string) => 
    api.get(`/companies/search/${query}`),
  
  getStats: () => 
    api.get('/companies/stats/overview'),
  
  updateStats: (id: string) => 
    api.patch(`/companies/${id}/update-stats`)
};

// Requests API
export const requestsAPI = {
  getAll: async (params?: any): Promise<{ items: any[], total: number }> => {
    const response = await api.get('/requests', { params });
    const { requests, pagination } = response.data.data;
    return {
      items: requests,
      total: pagination.total,
    };
  },
  
  getUrgent: () => 
    api.get('/requests/urgent'),
  
  getMy: () => 
    api.get('/requests/my'),
  
  getById: async (id: string) => {
    const res = await api.get(`/requests/${id}`);
    // Adapter au vrai shape renvoyé par ton backend:
    // ex 1: { success, data: { request } }
    return res.data.data.request;
  },
  
  create: (requestData: any) => 
    api.post('/requests', requestData),

  update: (id: string, requestData: any) => 
    api.put(`/requests/${id}`, requestData),
  
  delete: (id: string) => 
    api.delete(`/requests/${id}`),
  
  assign: (id: string, technicianId: string) => 
    api.patch(`/requests/${id}/assign`, { technicianId }),
  
  complete: (id: string, actualDuration?: number) => 
    api.patch(`/requests/${id}/complete`, { actualDuration }),
  
  getStats: () => 
    api.get('/requests/stats/overview'),
  
  convertToQuote: (id: string) => api.post(`/requests/${id}/convert-to-quote`),
};

// Quotes API
export const quotesAPI = {
  getAll: async (params?: any): Promise<{ items: any[], total: number }> => {
    const response = await api.get('/quotes', { params });
    const { quotes, pagination } = response.data.data;

    return {
      items: quotes,
      total: pagination.total,
    };
  },
  
  getMy: () => 
    api.get('/quotes/my'),
  
  getById: (id: string) => 
    api.get(`/quotes/${id}`),
  
  create: (quoteData: any) => 
    api.post('/quotes', quoteData),
  
  update: (id: string, quoteData: any) => 
    api.put(`/quotes/${id}`, quoteData),
  
  delete: (id: string) => 
    api.delete(`/quotes/${id}`),

  send: (id: string) => 
    api.post(`/quotes/${id}/send`),
  
  respond: (id: string, accepted: boolean, comments?: string) => 
    api.post(`/quotes/${id}/respond`, { accepted, comments }),
  downloadPdf: (id: string) =>
    api.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
  getStatsOverview: (params?: any) => 
    api.get('/quotes/stats/overview', { params }),
  add: (payload: any) => api.post('/quotes', payload),
  getByClientId: (clientId: string) => api.get(`/quotes/client/${clientId}`),
};

// Payments API
export const paymentsAPI = {
  getAll: async (q: Record<string,string|number|undefined>): Promise<{ items: any[]; total: number }> => {
    const params = new URLSearchParams();
    Object.entries(q).forEach(([k,v]) => { if (v !== undefined && v !== '') params.append(k, String(v)); });
    const r = await api.get(`/payments?${params.toString()}`);
    // Normalisation
    const items = r.data?.data?.items ?? [];
    const total = r.data?.data?.total ?? 0;
    return { items, total };
  },

  getMy: async (): Promise<{ items: any[]; total: number }> => {
    const response = await api.get('/payments/my');
    const items = response?.data?.data?.items ?? [];
    const total = response?.data?.data?.total ?? 0;
    return { items, total };
  },

  getById: (id: string) => api.get(`/payments/${id}`),
  create: (paymentData: any) => api.post('/payments', paymentData),
  update: (id: string, paymentData: any) => api.put(`/payments/${id}`, paymentData),
  delete: (id: string) => api.delete(`/payments/${id}`),
  downloadPdf: (id: string) => api.get(`/payments/${id}/invoice`, { responseType: 'blob' }),

  createPaymentIntent: (quoteId: string, paymentType: string, amount: number) =>
    api.post('/payments/create-payment-intent', { quoteId, paymentType, amount }),

  confirmPayment: (paymentIntentId: string) => api.post('/payments/confirm', { paymentIntentId }),
  getInvoice: (id: string) => api.get(`/payments/${id}/invoice`),
  getReceipt: (id: string) => api.get(`/payments/${id}/receipt`),
  getStats: () => api.get('/payments/stats/overview'),
  getInvoiceSummary: async (invoiceId: string): Promise<{ totalPaid: number; due: number; invoiceStatus: string }> => {
    const response = await api.get(`/payments/by-invoice/${invoiceId}/summary`);
    return response.data.data;
  },
};

// Missions API
export const missionsAPI = {
  getAll: async (params?: any): Promise<{ items: any[], total: number }> => {
    const response = await api.get('/missions', { params });
    const { missions, pagination } = response.data.data;
    return {
      items: missions,
      total: pagination.total,
    };
  },
  getMyMissions: async (params?: any): Promise<{ items: any[], total: number }> => {
    const response = await api.get('/missions/my-missions', { params });
    const { missions, pagination } = response.data.data;
    return {
      items: missions,
      total: pagination.total,
    };
  },
  getById: async (id: string) => {
    const response = await api.get(`/missions/${id}`);
    return response.data.data.mission;
  },
  create: (data: any) => 
    api.post('/missions', data),
  update: (id: string, data: any) => 
    api.put(`/missions/${id}`, data),
  delete: (id: string) => 
    api.delete(`/missions/${id}`),
  getStatsOverview: (params?: any) => 
    api.get('/missions/stats/overview', { params }),
};

// Reports API
export const reportsAPI = {
  getAll: async (params?: any): Promise<{ items: any[], total: number }> => {
    const response = await api.get('/reports', { params });
    const { reports, pagination } = response.data.data;
    return {
      items: reports,
      total: pagination.total,
    };
  },
  
  getMy: () => 
    api.get('/reports/my'),
  
  getById: (id: string) => 
    api.get(`/reports/${id}`),
  
  create: (reportData: FormData) => 
    api.post('/reports', reportData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
  
  update: (id: string, reportData: FormData) => 
    api.put(`/reports/${id}`, reportData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
  
  uploadPhotos: (id: string, formData: FormData) => 
    api.post(`/reports/${id}/photos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
  
  generatePDF: (id: string) => 
    api.get(`/reports/${id}/pdf`, { responseType: 'blob' }),
  
  sendToClient: (id: string) => 
    api.post(`/reports/${id}/send-to-client`),
  
  sendToBatuta: (id: string) => 
    api.post(`/reports/${id}/send-to-batuta`),
  
  generateCertificate: (id: string) => 
    api.post(`/reports/${id}/generate-certificate`),
  
  delete: (id: string) => 
    api.delete(`/reports/${id}`)
};

// Messages API
export const messagesAPI = {
  getConversations: () => 
    api.get('/messages/conversations'),
  
  getConversation: (id: string) => 
    api.get(`/messages/conversations/${id}`),
  
  createConversation: (conversationData: any) => 
    api.post('/messages/conversations', conversationData),
  
  sendMessage: (conversationId: string, content: string, attachments?: File[]) => {
    const formData = new FormData();
    formData.append('content', content);
    
    if (attachments && attachments.length > 0) {
      attachments.forEach(file => {
        formData.append('attachments', file);
      });
    }
    
    return api.post(`/messages/conversations/${conversationId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  markAsRead: (conversationId: string) => 
    api.patch(`/messages/conversations/${conversationId}/read`)
};



export const siteCustomizationAPI = {
  // GET (public): Fetches the projected public configuration
  getPublic: () => 
    api.get('/site-customization/public'),

  // GET (admin): Fetches the entire raw KV object for the admin panel
  getAll: () => 
    api.get('/site-customization'),

  // PUT (admin): Sends the entire object to be merged on the backend
  update: (data: any) => 
    api.put('/site-customization', data),

  // POST (admin): Uploads an image file
  uploadImage: (formData: FormData) =>
    api.post('/site-customization/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // DELETE (admin): Deletes an image from Cloudinary by its public ID
  deleteImage: (publicId: string) => 
    api.delete(`/site-customization/image/${publicId}`),

  // DELETE (admin): Deletes a nested entity (e.g., a featured service)
  deleteSubEntity: (key: string, identifier: Record<string, any>) =>
    api.delete('/site-customization/entity', { data: { key, identifier } }),
};

// Dashboard API
export const dashboardAPI = {
  getClientDashboard: () => 
    api.get('/dashboard/client'),
  getTechnicianDashboard: () => 
    api.get('/dashboard/technician'),
  getAdminStatsOverview: (params?: any) => 
    api.get('/dashboard/admin/stats-overview', { params }),
  getAdminMissionsByStatus: (params?: any) => 
    api.get('/dashboard/admin/missions-by-status', { params }),
  getAdminMissionsByTechnician: (params?: any) => 
    api.get('/dashboard/admin/missions-by-technician', { params }),
  getAdminMissionsByDate: (date: string) => 
    api.get(`/dashboard/admin/missions-by-date?date=${date}`),
  getAdminRecentMissionsWithReports: () => 
    api.get('/dashboard/admin/recent-missions-with-reports'),
  getAdminMonthlyStats: () => 
    api.get('/dashboard/admin/monthly-stats'),
};

// Equipments API
export const equipmentsAPI = {
  getAll: async (params?: any): Promise<{ items: any[], total: number }> => {
    const response = await api.get('/equipments', { params });
    const { equipments, pagination } = response.data.data;
    return {
      items: equipments,
      total: pagination.total,
    };
  },
  getById: (id: string) => 
    api.get(`/equipments/${id}`),
  create: (data: any) => 
    api.post('/equipments', data),
  update: (id: string, data: any) => 
    api.put(`/equipments/${id}`, data),
  delete: (id: string) => 
    api.delete(`/equipments/${id}`),
};

// Service Types API
// util commun pour normaliser
const normalizeList = (res: any, preferKeys: string[] = []) => {
  const d = res?.data ?? res;
  const items =
    preferKeys.map(k => d?.data?.[k]).find(Array.isArray) ??
    preferKeys.map(k => d?.[k]).find(Array.isArray) ??
    d?.data?.items ?? d?.items ?? d?.data?.results ?? d?.results ??
    d?.data?.list ?? d?.list ?? (Array.isArray(d?.data) ? d?.data : Array.isArray(d) ? d : []);
  const arr = Array.isArray(items) ? items : [];
  const total = d?.data?.total ?? d?.total ?? arr.length; // <- plus d'accès dangereux
  return { items: arr, total };
};

// Service Types API
export const serviceTypesAPI = {
  getAll: async (params?: any) => {
    const res = await api.get('/service-types?limit=1000', { params });
    return normalizeList(res, ['serviceTypes']);
  },
  getById: async (id: string) => {
    const response = await api.get(`/service-types/${id}`);
    console.log('Returning from getById:', response.data.data);
    return response.data.data; // Correctly extract the serviceType object
  },
  create: (data: any) =>
    api.post('/service-types', data),
  update: (id: string, data: any) =>
    api.put(`/service-types/${id}`, data),
  delete: (id: string) =>
    api.delete(`/service-types/${id}`),
};

// products
export const productsAPI = {
  getAll: async (params?: any) => {
    const res = await api.get('/products?limit=1000', { params });
    return normalizeList(res, ['products']);
  },
  getById: (id: string) =>
    api.get(`/products/${id}`),
  create: (data: any) =>
    api.post('/products', data),
  update: (id: string, data: any) =>
    api.put(`/products/${id}`, data),
  delete: (id: string) =>
    api.delete(`/products/${id}`),
  exportProducts: () =>
    api.get('/products/export/csv', { responseType: 'blob' }),
  importProducts: (formData: FormData) =>
    api.post('/products/import/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
};

// kits
export const kitsAPI = {
  getAll: async (params?: any) => {
    const res = await api.get('/kits?limit=1000', { params });
    return normalizeList(res, ['kits']);
  },
  getById: (id: string) => 
    api.get(`/kits/${id}`),
  create: (data: any) => 
    api.post('/kits', data),
  update: (id: string, data: any) => 
    api.put(`/kits/${id}`, data),
  delete: (id: string) => 
    api.delete(`/kits/${id}`),
};

// Invoices API
export const invoicesAPI = {
  getAll: async (params?: any): Promise<{ items: any[], total: number }> => {
    const response = await api.get('/invoices', { params });
    const { invoices, pagination } = response.data.data;
    return {
      items: invoices,
      total: pagination.total,
    };
  },
  getById: (id: string) => 
    api.get(`/invoices/${id}`),
  create: (data: any) => 
    api.post('/invoices', data),
  update: (id: string, data: any) => 
    api.put(`/invoices/${id}`, data),
  delete: (id: string) => 
    api.delete(`/invoices/${id}`),
  downloadPdf: (id: string) => 
    api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// Notifications API
export const notificationsAPI = {
  getMy: () => 
    api.get('/notifications/my'),
  markAsRead: (id: string) => 
    api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => 
    api.post('/notifications/mark-all-read'),
  delete: (id: string) => 
    api.delete(`/notifications/${id}`),
};

// Simulation API
export const simulationAPI = {
  send: (data: any) => 
    api.post('/simulation/send-email', data),
};

export default api;
